import { getGasConsumption } from "$/api/market-monitoring/fundamentals.api";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import type { GraphFilterItemsProps } from "$/lib/components/graphs/GraphFilter/GraphFilterItem";
import LineGraph from "$/lib/components/graphs/LineGraph";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import { RadioButtonSet } from "$/lib/components/molecules/RadioButtonSet";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";
import { cn, getArrayKeys } from "$/lib/utils/functions/misc.functions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

const GAS_COLORS = [
  "#933d2f",
  "#cc5d2f",
  "#e69440",
  "#eed36e",
  "#933d2f90",
  "#cc5d2f99",
  "#e6944090",
  "#eed36e90",
  "#933d2f80",
  "#cc5d2f80",
  "#e6944080",
  "#eed36e80",
];

const GasConsumptionChart = ({
  id,
  isDraggable = false,
  isShowcase = false,
}: CustomDashboardProps) => {
  const [filters, setFilters] = useState<{
    gasType: "all" | "NaTran" | "Teréga";
  }>({ gasType: "NaTran" });

  const { data, isLoading } = useQuery({
    queryKey: ["get-gas-consumption"],
    queryFn: () => getGasConsumption(),
  });

  const toDisplay = useMemo(
    () =>
      data
        ? filters.gasType === "all"
          ? data.allData
          : filters.gasType === "NaTran"
            ? data.natranGas
            : data.tGas
        : [],
    [data, filters],
  );

  const keys = useMemo(() => getArrayKeys(toDisplay).sort(), [toDisplay]);

  const last3Years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from(new Array(3), (_, i) => currentYear - i);
  }, []);

  const colorsPresets = useMemo(
    () => GAS_COLORS.slice(0, keys.length).reverse(),
    [keys],
  );

  const colors: Record<string, string> = keys.reduce(
    (acc, key, i) => ({
      ...acc,
      [key]: colorsPresets[i],
    }),
    {},
  );

  const [graphVisibility, setGraphVisibility] = useState<
    GraphFilterItemsProps[]
  >([]);

  const defaultGraphVisibilityKeys = useMemo(
    () =>
      keys.map((key, i) => ({
        name: key,
        isVisible: last3Years.includes(+key),
        color: colorsPresets[i],
      })),
    [keys, last3Years, colorsPresets],
  );

  const handleOnChangeGraphVisiblity = (key: string, value: boolean) => {
    if (key === "Tout") {
      if (value === true)
        setGraphVisibility([
          { name: "Tout", isVisible: true, color: "" },
          ...defaultGraphVisibilityKeys.map((item) => ({
            ...item,
            isVisible: true,
          })),
        ]);
      else
        setGraphVisibility([
          { name: "Tout", isVisible: false, color: "" },
          ...defaultGraphVisibilityKeys.map((item) => ({
            ...item,
            isVisible: false,
          })),
        ]);

      return;
    }

    setGraphVisibility((prev) =>
      prev.map((item) =>
        item.name === key ? { ...item, isVisible: value } : item,
      ),
    );
  };

  useEffect(() => {
    setGraphVisibility([
      { name: "Tout", isVisible: false, color: "" },
      ...defaultGraphVisibilityKeys,
    ]);
  }, [keys, defaultGraphVisibilityKeys]);

  return (
    <DisplayBox
      title="Consommation de gaz naturel en France"
      containerClassName={cn("shadow-displayBoxShadow", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      Icon={LineChartIcon}
      theme="orange"
      id={id}
      isDraggable={isDraggable}
    >
      <ChartsContainer className="!h-[510px] py-0">
        {isLoading ? (
          <Flexbox fullWidth fullHeight align="center" justify="center">
            <Loader height={40} width={40} />
          </Flexbox>
        ) : (
          <LineGraph
            data={toDisplay}
            xAxisDataKey="date"
            colors={colors}
            graphVisibility={graphVisibility}
            unit=" GW"
            theme="orange"
            keys={keys}
            leftMargin={-10}
            hasCustomXAxisDomain={false}
            hasReferences={false}
          />
        )}

        <FiltersDisplayBox className="min-w-[15%]">
          <RadioButtonSet<"all" | "NaTran" | "Teréga">
            id="gasType"
            title="Opérateurs"
            selectedId={filters.gasType}
            onChange={(id) => setFilters((prev) => ({ ...prev, gasType: id }))}
            options={[
              { id: "all", label: "(Tout)" },
              { id: "NaTran", label: "NaTran" },
              { id: "Teréga", label: "Teréga" },
            ]}
          />

          <GraphFilter
            title="Légende"
            data={graphVisibility}
            handleOnChange={handleOnChangeGraphVisiblity}
          />
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default GasConsumptionChart;
